import { Component, signal, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { Token } from '@app/auth/token.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-user-email-change',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    MatStepperModule,
  ],
  standalone: true,
  templateUrl: './user-email-change.component.html',
  styleUrl: './user-email-change.component.scss',
})
export class UserEmailChangeComponent implements OnDestroy {
  emailForm!: FormGroup;
  userForm!: FormGroup;

  step = signal(0);

  token: Token | null = null;
  user: any = null;

  resendEmailCodeTimerInitial = 30;
  resendEmailCodeInterval: any = null;
  resendEmailCodeTimer = signal(0);

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private authService: AuthService,
    private router: Router
  ) {
    if (!environment.allowEmailChange) {
      this.router.navigate(['/account']);
      return;
    }

    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.userForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      token_code: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      token_uuid: ['', Validators.required],
    });

    this.user = this.authService.getUserFromStorage();

    if (this.user) {
      this.emailForm.patchValue({
        email: this.user.email,
      });
    }
  }

  createToken(): void {
    if (this.emailForm.invalid) {
      this.appService.showSnackBar('Please enter a valid email address', 'error');
      return;
    }

    if (!this.formChanged()) {
      this.appService.showSnackBar('No changes made', 'info');
      return;
    }

    const data = {
      identifier: this.emailForm.value.email,
      unique_user_identifier: true,
      delivery_method: 'email',
    };

    this.token = null;

    this.appService.post('create-token', data).subscribe({
      next: (response) => {
        this.token = response.data;

        this.step.set(1);

        this.startEmailCodeResendTimer();

        this.userForm.patchValue({
          email: this.emailForm.value.email,
          token_uuid: this.token?.uuid,
        });
      },
      error: (error) => {
        this.appService.handleError(error);
      },
    });
  }

  updateEmail(): void {
    if (this.userForm.invalid) {
      this.appService.showSnackBar('Please enter a valid email address', 'error');
      return;
    }

    this.authService.updateUserEmail(this.userForm.value).subscribe({
      next: (response) => {
        this.appService.showSnackBar(response.message, 'success');
        this.authService.setUserToStorage(response.data);
        this.router.navigate(['/account']);
      },
      error: (error) => {
        this.appService.handleError(error);
      },
    });
  }

  startEmailCodeResendTimer(): void {
    this.resendEmailCodeTimer.set(this.resendEmailCodeTimerInitial);

    this.resendEmailCodeInterval = setInterval(() => {
      if (this.resendEmailCodeTimer() > 0) {
        this.resendEmailCodeTimer.set(this.resendEmailCodeTimer() - 1);
      } else {
        this.clearEmailCodeResendTimer();
      }
    }, 1000);
  }

  clearEmailCodeResendTimer(): void {
    if (this.resendEmailCodeInterval) {
      clearInterval(this.resendEmailCodeInterval);
      this.resendEmailCodeInterval = null;
    }
  }

  changeEmail(): void {
    this.emailForm.reset();

    if (this.user) {
      this.emailForm.patchValue({
        email: this.user.email,
      });
    }

    this.userForm.reset();
    this.token = null;
    this.step.set(0);
    this.clearEmailCodeResendTimer();
  }

  formChanged(): boolean {
    const email = this.emailForm.get('email')?.value;

    if (this.user) {
      return email !== this.user.email;
    }

    return false;
  }

  goBack(): void {
    this.router.navigate(['/account']);
  }

  ngOnDestroy(): void {
    this.clearEmailCodeResendTimer();
  }
}